import { GetCmsNews, GetCmsNewsItem } from '@cbgms/api/modules/cms/requests'
import { useInfiniteQuery, useQuery, useQueryClient } from '@cbgms/base/http/use-query'
import { useLocaleContext } from '@cbgms/base/locale/LocaleContext'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'

const CMSNewsListCacheKey = (orderAccountUUID: string, locale: string, perPage: number) => [
  'CMS',
  'NEWS_ITEMS',
  'LIST',
  orderAccountUUID,
  locale,
  perPage
]

const CMSNewsItemCacheKey = (orderAccountUUID: string, locale: string, id: string) => [
  'CMS',
  'NEWS_ITEMS',
  'item',
  orderAccountUUID,
  locale,
  id
]

export const useCMSNewsList = (perPage = 10) => {
  const dispatch = useDispatch()
  const { locale } = useLocaleContext()
  const { selectedOrderAccount } = useOrderAccountsContext()
  const queryClient = useQueryClient()

  const queryData = useInfiniteQuery(
    CMSNewsListCacheKey(selectedOrderAccount.UUID, locale, perPage),
    async ({ pageParam = 0 }) => {
      const { Data } = await dispatch(GetCmsNews({ orderAccountUUID: selectedOrderAccount.UUID, page: pageParam + 1, perPage }))
      // after fetching apply some pre cached data so we don't need to fetch it every time
      Data.News.forEach(item => queryClient.setQueryData(CMSNewsItemCacheKey(selectedOrderAccount.UUID, locale, item.ID), item))
      return { newsItems: Data.News, nextCursor: pageParam + 1 }
    },
    { getNextPageParam: lastPage => (lastPage.newsItems.length ? lastPage.nextCursor : undefined) }
  )

  const allNewsItems = useMemo(() => (queryData.data?.pages || []).flatMap(p => p.newsItems), [queryData.data?.pages])
  return useMemo(() => ({ ...queryData, allNewsItems, hasNextPage: !!queryData.hasNextPage }), [queryData])
}

export const useCMSNewsItem = (id: string) => {
  const dispatch = useDispatch()
  const { locale } = useLocaleContext()
  const { selectedOrderAccount } = useOrderAccountsContext()

  return useQuery(CMSNewsItemCacheKey(selectedOrderAccount.UUID, locale, id), async () => {
    const { Data } = await dispatch(GetCmsNewsItem({ orderAccountUUID: selectedOrderAccount.UUID, id }))
    return Data
  })
}
